<script lang="ts">
import type { ThemeSlots } from '#layers/ui/types/tv';
import type { ButtonProps } from './Button.vue';
import type { ColorPickerProps } from './ColorPicker.vue';
import type { InputProps } from './Input.vue';
import { tv } from 'tailwind-variants';
import { isNil, isNullishOrEmpty } from '#layers/base/public';

const inputColor = tv({
	slots: {
		base: 'grid shrink-0 grid-cols-[auto_1fr] gap-2.5',
		input: 'flex-1',
	},
	variants: {
		block: {
			true: {
				base: 'w-full',
			},
		},
	},
});

export interface InputColorProps {
	name?: string;
	disabled?: boolean;
	readonly?: boolean;
	placeholder?: string;
	defaultValue?: string;
	/**
	 * Automatically select the first preset as the default value and emit it on being mounted.
	 */
	defaultValuePreset?: boolean;
	presets?: string[];
	/**
	 * Allow the selected color to be cleared (set the value to `null`).
	 *
	 * @default true
	 */
	clear?: boolean;
	color?: ButtonProps['color'];
	block?: boolean;
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	variant?: InputProps['variant'] & ButtonProps['variant'];
	ui?: ThemeSlots<typeof inputColor>;
}
</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core';
import { ColorTranslator } from 'colortranslator';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFormField } from '#imports';
import { DEFAULT_COLORS, getBorderColor } from '#layers/ui/utils/colors';

const props = withDefaults(defineProps<InputColorProps>(), {
	presets: () => [...DEFAULT_COLORS, '#000000', '#9CA7B6', '#FFFFFF'],
	variant: 'outline',
	clear: true,
});

const modelValue = defineModel<string | null>();
const format = defineModel<ColorPickerProps['format']>('format', { default: 'hex' });
const forwarded = reactivePick(props, 'name', 'disabled', 'readonly', 'placeholder', 'color', 'variant', 'block');

const { t } = useI18n();
const { emitFormInput, emitFormBlur, size } = useFormField<InputColorProps>(props);

function setColorValue(value: string | null | undefined): boolean {
	// Treat both `null` and `undefined` as "no value"; only write real strings.
	if (!isNil(value)) {
		modelValue.value = value;
		return true;
	}

	if (props.clear) {
		modelValue.value = null;
		return true;
	}

	return false;
}

onMounted(() => {
	if (props.defaultValuePreset && props.presets[0] && modelValue.value === undefined) {
		setColorValue(props.presets[0]);
	}
});

const colorTranslator = computed({
	get() {
		try {
			return new ColorTranslator(modelValue.value ?? props.defaultValue ?? props.presets[0] ?? '#FFFFFF');
		}
		catch {
			return null;
		}
	},
	set(value) {
		if (setColorValue(value?.HEX)) {
			emitFormInput();
		}
	},
});

const theme = computed(() => inputColor({ block: props.block }));

function updateColor() {
	setColorValue(colorTranslator.value?.HEX);
}

const hexPattern = /^#[A-F0-9]{0,6}$/i;

const localHexValue = ref<string | null>(null);
const open = ref(false);

// The color picker is always mounted and echoes a fallback color back into the
// model whenever the model becomes empty. Ignore those echoes after a clear
// until the user intentionally reopens the popover.
const suppressPickerEcho = ref(false);

watch(open, (isOpen) => {
	if (isOpen) suppressPickerEcho.value = false;
});

function handlePickerUpdate(value: string | undefined) {
	if (suppressPickerEcho.value) return;
	setColorValue(value);
}

// Use `||` so an empty draft (`localHexValue === ''`) falls back to the saved color.
const previewColor = computed(() => localHexValue.value || modelValue.value || props.defaultValue);

const previewBorderColor = computed(() => getBorderColor(previewColor.value));
const previewHoverBorderColor = computed(() => getBorderColor(previewColor.value, 30));

// The !important is (unfortunately) necessary to override the default background color of disabled buttons..
const previewBackgroundColor = computed(() => `${previewColor.value || 'transparent'} !important`);

const readonlyPreviewStyle = computed(() => ({
	'backgroundColor': previewBackgroundColor.value,
	'--tw-ring-color': previewBorderColor.value,
}));

const triggerPreviewStyle = computed(() => ({
	'backgroundColor': previewBackgroundColor.value,
	'--swatch-border': previewBorderColor.value ?? 'var(--ui-border)',
	'--swatch-hover-border': previewHoverBorderColor.value ?? 'var(--ui-border-accented)',
}));

function handleHexInput(value: string | null) {
	if (value === null || value === '') {
		if (props.clear) {
			setColorValue(null);
			localHexValue.value = null;
		}
		else {
			// Keep an empty replacement as a local draft. The saved value remains
			// intact unless the user enters a valid color before leaving the input.
			localHexValue.value = '';
		}

		return;
	}

	if (!hexPattern.test(value)) {
		return;
	}

	localHexValue.value = value;
}

function handleHexBlur() {
	if (localHexValue.value) {
		try {
			setColorValue(ColorTranslator.toHEX(localHexValue.value));
		}
		catch {}
	}

	localHexValue.value = null;
}

function handlePresetSelect(color: string | null | undefined) {
	if (setColorValue(color)) {
		emitFormInput();
	}
}

function clearColor() {
	if (!props.clear) return;
	suppressPickerEcho.value = true;
	setColorValue(null);
	localHexValue.value = null;
	emitFormInput();
	open.value = false;
}

function handlePopoverToggle(isOpen: boolean) {
	if (isOpen) return;
	// Resolve any pending hex draft on close so an emptied non-clearable field
	// restores its saved color even when the input never received a DOM blur.
	handleHexBlur();
	emitFormBlur();
}

const formatOptions = [
	{
		value: 'hex',
		label: t('input_color.format.hex'),
	},
	{
		value: 'rgb',
		label: t('input_color.format.rgb'),
	},
	{
		value: 'hsl',
		label: t('input_color.format.hsl'),
	},
	{
		value: 'lab',
		label: t('input_color.format.lab'),
	},
];
</script>

<template>
	<div v-if="props.readonly" :class="theme.base({ class: [props.ui?.base] })">
		<div
			class="aspect-square shrink-0 rounded-md ring ring-accented ring-inset"
			:style="readonlyPreviewStyle"
		/>
		<DInput
			v-bind="forwarded"
			:size
			readonly
			:class="theme.input({ class: [props.ui?.input] })"
			:model-value="localHexValue ?? modelValue ?? defaultValue"
		/>
	</div>
	<DPopover
		v-else
		v-model:open="open"
		mode="click"
		:content="{
			align: 'start',
		}"
		:disabled
		@update:open="handlePopoverToggle"
	>
		<div :class="theme.base({ class: [props.ui?.base] })">
			<DButton
				color="neutral"
				class="
					aspect-square h-full ring-(--swatch-border)
					focus-visible:ring-1 focus-visible:ring-primary
					disabled:ring-(--swatch-border)!
					interactable-hover:ring-(--swatch-hover-border)
				"
				:style="triggerPreviewStyle"
				:disabled
				:size
				:variant
			/>
			<DInput
				v-bind="forwarded"
				:size
				:class="theme.input({ class: [props.ui?.input] })"
				:model-value="localHexValue ?? modelValue ?? defaultValue"
				:ui="{ trailing: 'pe-1' }"
				@update:model-value="handleHexInput($event as string | null)"
				@blur="handleHexBlur"
			>
				<template v-if="clear && modelValue && !disabled" #trailing>
					<DTooltip :text="$t('common.clear')">
						<DButton
							data-testid="color-clear"
							icon="lucide:x"
							color="neutral"
							variant="link"
							size="sm"
							:aria-label="t('input_color.remove_color')"
							@click.stop.prevent="clearColor"
						/>
					</DTooltip>
				</template>
			</DInput>
		</div>

		<template #content>
			<DAnimatedSheet class="flex max-w-66 flex-col items-stretch gap-4 overflow-hidden rounded-lg p-4">
				<DColorPicker
					:model-value="modelValue ?? undefined"
					:default-value
					:ui="{ selector: 'w-58', track: 'w-50', trackContainer: 'items-center gap-2' }"
					orientation="horizontal"
					@update:model-value="handlePickerUpdate"
				>
					<template #track-leading>
						<DColorDot v-if="modelValue" :color="modelValue" />
					</template>
				</DColorPicker>
				<DSelect v-model="format" :items="formatOptions" />
				<div class="grid grid-cols-3 gap-x-2">
					<template v-if="format === 'hex'">
						<DInput
							:model-value="localHexValue ?? colorTranslator?.HEX"
							:model-options="{ nullify: true }"
							class="col-span-full"
							@update:model-value="handleHexInput($event as string | null)"
							@blur="handleHexBlur"
						/>
					</template>
					<template v-else-if="format === 'rgb'">
						<DInput
							variant="outline"
							:model-modifiers="{ number: true }"
							:model-value="colorTranslator?.R ?? null"
							@update:model-value="(v) => {
								colorTranslator?.setR(Number(v)); updateColor();
							}"
						>
							<template #trailing>
								R
							</template>
						</DInput>
						<DInput
							variant="outline"
							:model-modifiers="{ number: true }"
							:model-value="colorTranslator?.G ?? null"
							@update:model-value="(v) => {
								colorTranslator?.setG(Number(v)); updateColor();
							}"
						>
							<template #trailing>
								G
							</template>
						</DInput>
						<DInput
							variant="outline"
							:model-modifiers="{ number: true }"
							:model-value="colorTranslator?.B ?? null"
							@update:model-value="(v) => {
								colorTranslator?.setB(Number(v)); updateColor();
							}"
						>
							<template #trailing>
								B
							</template>
						</DInput>
					</template>
					<template v-else-if="format === 'hsl'">
						<DInput
							variant="outline"
							:model-modifiers="{ number: true }"
							:model-value="colorTranslator?.H ?? null"
							@update:model-value="(v) => {
								colorTranslator?.setH(Number(v)); updateColor();
							}"
						>
							<template #trailing>
								H
							</template>
						</DInput>
						<DInput
							variant="outline"
							:model-modifiers="{ number: true }"
							:model-value="colorTranslator?.S ?? null"
							@update:model-value="(v) => {
								colorTranslator?.setS(Number(v)); updateColor();
							}"
						>
							<template #trailing>
								S
							</template>
						</DInput>
						<DInput
							variant="outline"
							:model-modifiers="{ number: true }"
							:model-value="colorTranslator?.L ?? null"
							@update:model-value="(v) => {
								colorTranslator?.setL(Number(v)); updateColor();
							}"
						>
							<template #trailing>
								L
							</template>
						</DInput>
					</template>
					<template v-else-if="format === 'lab'">
						<DInput
							variant="outline"
							:model-modifiers="{ number: true }"
							:model-value="colorTranslator?.CIEL ?? null"
							@update:model-value="(v) => {
								colorTranslator?.setCIEL(Number(v)); updateColor();
							}"
						>
							<template #trailing>
								L
							</template>
						</DInput>
						<DInput
							variant="outline"
							:model-modifiers="{ number: true }"
							:model-value="colorTranslator?.CIEa ?? null"
							@update:model-value="(v) => {
								colorTranslator?.setCIEa(Number(v)); updateColor();
							}"
						>
							<template #trailing>
								A
							</template>
						</DInput>
						<DInput
							variant="outline"
							:model-modifiers="{ number: true }"
							:model-value="colorTranslator?.CIEb ?? null"
							@update:model-value="(v) => {
								colorTranslator?.setCIEb(Number(v)); updateColor();
							}"
						>
							<template #trailing>
								B
							</template>
						</DInput>
					</template>
				</div>
				<DColorSwatches
					v-if="!isNullishOrEmpty(presets)"
					:presets
					:model-value
					@update:model-value="handlePresetSelect"
				/>
				<div v-if="clear && modelValue" class="-mx-4 -mb-4 flex justify-end border-t border-default p-2">
					<DButton
						variant="ghost"
						color="neutral"
						class="font-normal"
						:aria-label="t('input_color.remove_color')"
						@click="clearColor"
					>
						{{ t('input_color.remove_color') }}
					</DButton>
				</div>
			</DAnimatedSheet>
		</template>
	</DPopover>
</template>
